import { useMemo } from "react";
import HomeWorkOutlinedIcon from '@mui/icons-material/HomeWorkOutlined';
import { BaseWidget } from "./BaseWidget";
import { PeriodOverrideControl } from "../controls/PeriodOverrideControl";
import { TextControl } from "../controls/TextControl";
import useSharedCollectionWhere from "../../../../firebase/useSharedCollectionWhere";
import useSettings from "../../../../hooks/Settings";
import MetricVisualization from "../../../dataVisualizations/MetricVisualization";
import ModulePlaceholder from "./_shared/ModulePlaceholder";
import isTemplateMode from "./_shared/isTemplateMode";
import useWidgetPeriod from "./_shared/useWidgetPeriod";

// AI-CONTEXT: See docs/features/livereport-widgets-housevisits-personas.md
// HouseVisitsTotalWidget — KPI big-number widget for the Huishoudens module.
// Shows the count of `Tasks` (Type=addressVisit) that fall within the editor-
// configured date range, with a customisable label below.
//
// Gating:
//   - getRequiredModule() = 'housevisits' → WidgetBrowser hides the tile
//     when the tenant has Huishoudens off.
//   - Renderer also short-circuits to <ModulePlaceholder/> when the module
//     flag is off at render time (defensive — a report saved on a
//     housevisits tenant may still be opened later when the module is
//     disabled), or when the editor is in template-preview mode.

/**
 * Format a `{start, end}` range as a Dutch sublabel. Returns null when the
 * range is unbounded (we don't want a sublabel of "alles" cluttering the KPI).
 *
 * @param {{ start: string|null, end: string|null }} range
 * @returns {string|null}
 */
const formatRangeSublabel = (range) => {
    if (!range || (!range.start && !range.end)) return null;
    if (range.start && range.end) return `${range.start} t/m ${range.end}`;
    if (range.start) return `Vanaf ${range.start}`;
    return `Tot ${range.end}`;
};

/**
 * Filter Tasks by an ISO-date range. Tasks whose `Timestamp` is missing or
 * unparsable are excluded — they can't be placed in the range.
 *
 * @param {Array<{Timestamp?: { toDate: () => Date }}>} tasks
 * @param {{ start: string|null, end: string|null }} range
 * @returns {number}
 */
const countTasksInRange = (tasks, range) => {
    const arr = Array.isArray(tasks) ? tasks : [];
    const start = range?.start ? new Date(range.start + 'T00:00:00') : null;
    const end = range?.end ? new Date(range.end + 'T23:59:59.999') : null;
    if (!start && !end) return arr.length;

    return arr.filter((t) => {
        const ts = t?.Timestamp;
        if (!ts || typeof ts.toDate !== 'function') return false;
        const d = ts.toDate();
        if (start && d < start) return false;
        if (end && d > end) return false;
        return true;
    }).length;
};

/**
 * @extends {BaseWidget}
 */
export class HouseVisitsTotalWidget extends BaseWidget {

    getName() { return 'Totaal aantal huisbezoeken'; }
    getCategory() { return 'huishoudens'; }
    getRequiredModule() { return 'housevisits'; }
    getIcon() { return <HomeWorkOutlinedIcon />; }

    getControls() {
        return {
            /* Migrated from DateRangeControl: same 'dateRange' key so legacy
               saved {start,end} values keep working (mode is derived). */
            'dateRange': new PeriodOverrideControl({
                label: 'Periode',
                description: 'Welke periode de telling toont.',
                defaultValue: { mode: 'inherit', start: null, end: null },
            }),
            'label': new TextControl({
                label: 'Label',
                description: 'Tekst die onder het getal wordt getoond.',
                defaultValue: 'Huisbezoeken',
            }),
        };
    }

    render(props) {
        return <Renderer {...props} />;
    }
}

/**
 * @param {object} props
 * @param {{ mode?: string, start: string|null, end: string|null }} [props.dateRange] - Widget period setting (PeriodOverrideControl; legacy values carry no mode)
 * @param {string} [props.label]
 * @returns {JSX.Element}
 */
const Renderer = ({ dateRange, label }) => {
    const { housevisits } = useSettings();
    const isTemplate = isTemplateMode();
    const tasks = useSharedCollectionWhere('Tasks', 'Type', 'addressVisit');
    const { range } = useWidgetPeriod(dateRange);

    const total = useMemo(
        () => countTasksInRange(tasks, range),
        [tasks, range]
    );

    if (isTemplate) {
        return <ModulePlaceholder mode="template" />;
    }
    if (housevisits !== true) {
        return <ModulePlaceholder mode="disabled" moduleLabel="Huishoudens" />;
    }

    return (
        <MetricVisualization
            value={total}
            label={label || 'Huisbezoeken'}
            sublabel={formatRangeSublabel(range)}
        />
    );
};

export { countTasksInRange, formatRangeSublabel };
